import { useState } from "react"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { Icon, cn } from "@nexus/ui"

import type { VendorHandoffCheck } from "@nexus/contracts"

import {
  formatVendorHandoffCheck,
  groupVendorHandoffChecks,
} from "@/features/marketplace/lib/vendor-handoff-check-copy"

function FieldChips({ labels }: { labels: string[] }) {
  return (
    <ul className="mt-1 flex flex-wrap gap-1">
      {labels.map((label) => (
        <li
          key={label}
          className="rounded-sm bg-background/70 px-1.5 py-0.5 text-[11px] font-medium text-foreground"
        >
          {label}
        </li>
      ))}
    </ul>
  )
}

function warningSummaryParts(
  groups: ReturnType<typeof groupVendorHandoffChecks>,
  t: TFunction,
): string[] {
  const parts: string[] = []
  if (groups.placeholders.length > 0) {
    parts.push(
      t("marketplace.sendHandoff.checksUnfinished", {
        count: groups.placeholders.length,
      }),
    )
  }
  if (groups.internalLinks.length > 0) {
    parts.push(
      t("marketplace.sendHandoff.checksBlockedLink", {
        count: groups.internalLinks.length,
      }),
    )
  }
  for (const item of groups.other) {
    parts.push(
      item.code === "missing_due_date"
        ? t("marketplace.sendHandoff.checksMissingDueDate", { defaultValue: "No due date" })
        : item.message,
    )
  }
  return parts
}

export function VendorHandoffChecks({
  blockers,
  warnings,
}: {
  blockers: VendorHandoffCheck[]
  warnings: VendorHandoffCheck[]
}) {
  const { t } = useTranslation()
  const warningGroups = groupVendorHandoffChecks(warnings, t)
  const summaryParts = warningSummaryParts(warningGroups, t)
  const warningCount = warnings.length
  const [warningsOpen, setWarningsOpen] = useState(warningCount <= 2)

  return (
    <div className="flex shrink-0 flex-col gap-2" aria-live="polite">
      {blockers.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2"
        >
          <p className="text-xs font-medium text-destructive">
            {t("marketplace.sendHandoff.checksBlockerTitle", "Can't send yet")}
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {blockers.map((check) => (
              <li key={check.code + check.message} className="text-[11px] text-destructive">
                {formatVendorHandoffCheck(check, t)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {warningCount > 0 ? (
        <details
          className="rounded-md border border-warning-border bg-warning-subtle px-2.5 py-2"
          open={warningsOpen}
          onToggle={(event) => setWarningsOpen(event.currentTarget.open)}
        >
          <summary
            className={cn(
              "cursor-pointer text-xs font-medium text-warning-subtle-foreground",
              "marker:text-warning-subtle-foreground",
            )}
          >
            <span className="inline-flex items-start gap-1.5 align-top">
              <Icon name="alert-circle" size="xs" color="inherit" className="mt-0.5" aria-hidden />
              <span>
                <span className="block">
                  {t("marketplace.sendHandoff.checksReviewTitle", "Review before sending")}
                </span>
                <span className="mt-0.5 block text-[11px] font-normal leading-relaxed text-warning-subtle-foreground/90">
                  {summaryParts.join(" · ")}
                </span>
              </span>
            </span>
          </summary>

          <div className="mt-2 flex flex-col gap-2 border-t border-warning-border/60 pt-2">
            {warningGroups.placeholders.length > 0 ? (
              <div>
                <p className="text-[11px] text-warning-subtle-foreground">
                  {t(
                    "marketplace.sendHandoff.checksPlaceholderHeading",
                    "Looks like placeholder text",
                  )}
                </p>
                <FieldChips labels={warningGroups.placeholders} />
              </div>
            ) : null}
            {warningGroups.internalLinks.length > 0 ? (
              <div>
                <p className="text-[11px] text-warning-subtle-foreground">
                  {t(
                    "marketplace.sendHandoff.checksInternalLinkHeading",
                    "Production partners cannot open these links",
                  )}
                </p>
                <FieldChips labels={warningGroups.internalLinks} />
              </div>
            ) : null}
            {warningGroups.other.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {warningGroups.other.map((item) => (
                  <li key={item.code + item.message} className="text-[11px] text-foreground">
                    {item.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </details>
      ) : null}
    </div>
  )
}
